import axios from "axios";
import { toast } from "react-toastify";
import { logoutUser } from "../helper/generalHelper";
import { monitorResponseHeaders } from "../helper/apiHelper";

// Base URL of your API
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// Automatically attach token if available
// Automatically attach token if available
axiosInstance.interceptors.request.use((config) => {
  // Token is handled via HttpOnly cookies
  // ✅ Dynamically rewrite API prefixes if we are in the staff portal
  const isStaffRoute = window.location.pathname.includes('/restaurant-staff');
  if (isStaffRoute && config.url) {
    // If the URL starts with "restaurant/" and is not the staff-login endpoint, rewrite it to "restaurant-staff/"
    if (config.url.startsWith("restaurant/") && !config.url.includes("staff-login")) {
      config.url = config.url.replace(/^restaurant\//, "restaurant-staff/");
    }
  }

  // ✅ Smart content-type handling
  if (config.data instanceof FormData) {
    // Delete explicit content-type so browser sets boundary automatically
    delete config.headers["Content-Type"];
  } else if (
    config.data &&
    typeof config.data === "object" &&
    !config.headers["Content-Type"]
  ) {
    config.headers["Content-Type"] = "application/json";
  }

  return config;
});

// Automatically handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (window.location.pathname !== '/restaurant/login' && window.location.pathname !== '/restaurant-staff/employee/staffLogin') {
          localStorage.removeItem('is_authenticated');
          localStorage.removeItem('userData');
          sessionStorage.setItem('logout_message_type', 'expired');
          const isStaff = window.location.pathname.includes('/restaurant-staff');
          window.location.href = isStaff ? '/restaurant-staff/employee/staffLogin' : '/restaurant/login';
      }
    }
    return Promise.reject(error);
  }
);

// Unified response handler
const handleResponse = async (apiCall, status_code_validate = true) => {
  try {
    const response = await apiCall;
    monitorResponseHeaders(response);
    return {
      success: true,
      data: response?.data?.data || null,
      message: response.data?.message,
      status: response.status,
    };
  } catch (error) {
    // if (status_code_validate) {
    //   // Inside your catch block
    //   const redirectStatusCodes = [401];

    //   if (redirectStatusCodes.includes(error?.response?.status)) {
    //     logoutUser();
    //     toast.error("Your session has expired. Please log in again.");
    //     window.location.href = "/"; // Redirect to login/home
    //   }
    // }
    
    console.error("API ERROR:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });

    const errorData = error?.response?.data;
    const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message;

    // Automatically notify user of permission denials globally (CTO minimal change)
    if (error?.response?.status === 403) {
      toast.error("You do not have permission to perform this action.");
    }

    return {
      success: false,
      error: errorData || error.message || "Unknown error",
      message: errorMessage || (error.message ? `Unknown error: ${error.message}` : "Unknown error"),
      data: errorData?.data || null,
      status: error?.response?.status || 500,
    };
  }
};

// Method-specific API functions
const api = {
  get: (endpoint, config = {}, status_code_validate = true) =>
    handleResponse(axiosInstance.get(endpoint, config), status_code_validate),

  post: (endpoint, data = {}, config = {}, status_code_validate = true) =>
    handleResponse(
      axiosInstance.post(endpoint, data, config),
      status_code_validate
    ),

  put: (endpoint, data = {}, config = {}, status_code_validate = true) =>
    handleResponse(
      axiosInstance.put(endpoint, data, config),
      status_code_validate
    ),

  delete: (endpoint, config = {}, status_code_validate = true) =>
    handleResponse(
      axiosInstance.delete(endpoint, config),
      status_code_validate
    ),
};

export default api;
